import * as Yup from 'yup';
import { styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

type ChildAgeValidationMessages = Readonly<{
  childAgeMin: string;
  childAgeMax: string;
  childAgeInvalid: string;
  childAgeRequired: string;
}>;

export type { ChildAgeValidationMessages };

const createChildrenAgesSchema = (validation: ChildAgeValidationMessages) =>
  Yup.array().of(
    Yup.number().nullable().typeError(validation.childAgeInvalid).required(validation.childAgeRequired).min(0, validation.childAgeMin).max(17, validation.childAgeMax),
  );

const createRoomSchema = (validation: ChildAgeValidationMessages) =>
  Yup.object({
    adults: Yup.number(),
    childrenAges: createChildrenAgesSchema(validation),
  });

export const createOccupancySchema = (validation: ChildAgeValidationMessages) => {
  const roomsSchema = Yup.array().of(createRoomSchema(validation));
  return roomsSchema.test('occupancy', 'Invalid occupancy', function (value) {
    if (!value) {
      return false;
    }
    return value.length > 0;
  });
};

export const AGE_OPTIONS = Array.from({ length: 18 }, (_, i) => i);

// eslint-disable-next-line no-restricted-syntax -- pre-existing native field, pending phase 5 of doc/field-focus-consistency/PLAN.md
export const StyledAgeSelect = styled('select')`
  width: 100%;
  max-width: ${pxToRem(160)};
  height: ${pxToRem(40)};
  padding: ${pxToRem(8)} ${pxToRem(12)};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${pxToRem(8)};
  font-size: ${pxToRem(14)};
  font-family: inherit;
  background-color: white;
  cursor: pointer;
  outline: none;

  /*
   * Was a newUIBrandPrimary border and shadow on :focus -- a brand colour on a field, which the
   * contract does not allow, and on :focus rather than :focus-visible. Same 2px footprint as before
   * (1px border + 1px shadow, this select having no wrapper to inset a ring against), now neutral.
   * See doc/field-focus-consistency/PLAN.md.
   */
  &:hover {
    border-color: ${({ theme }) => theme.colors.mono6};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.mono6};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.inputBorderFocused};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.inputBorderFocused};
  }

  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.colors.inputBorderError};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.inputBorderError};
  }
`;
